/* EcosystemGraphModal — Schéma d'écosystème DÉRIVÉ des objets/relations validés
 * (jamais du Mermaid libre LLM). Chaque nœud conserve provenance + statut
 * épistémique et navigue vers sa fiche canonique (OntologyPanel). */
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { X, Network, Loader2 } from "lucide-react";
import ContextStack from "@/components/ContextStack";
import { ontologyPanel } from "@/components/OntologyPanel";
import {
  ReactFlow, Background, Handle, Position, MarkerType,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";

const OTYPE_META = {
  organisation: { color: "#378ADD", label: "Organisation" },
  person: { color: "#9CA3AF", label: "Personne" },
  risk: { color: "#E24B4A", label: "Risque" },
  action: { color: "#1D9E75", label: "Plan d'action" },
  deal: { color: "#7F77DD", label: "Opportunité" },
  control: { color: "#22C55E", label: "Contrôle" },
  incident: { color: "#F97316", label: "Incident" },
};
const EPI_COLOR = { declared: "#1D9E75", inferred: "#EF9F27", source: "#378ADD" };
const EPI_LABEL = { declared: "Déclaré", inferred: "Inféré", source: "Source" };
// Types réellement navigables via GET /workspace/context/{otype}/{oid}
const NAV_OTYPE = { organisation: "entity", person: "person", risk: "risk", control: "control" };

function EcoNode({ data }) {
  const meta = OTYPE_META[data.otype] || { color: "#666", label: data.otype || "objet" };
  return (
    <div
      className={`rounded-md bg-[#0E0E10] border px-2.5 py-1.5 shadow-md transition-colors ${
        data.clickable ? "cursor-pointer hover:border-white/40" : ""
      } ${data.exists ? "" : "opacity-50"}`}
      style={{ width: 190, borderColor: `${meta.color}66` }}
      onClick={data.onOpen}
      data-testid={`eco-node-${data.oid}`}
    >
      <Handle type="target" position={Position.Left} style={{ background: meta.color, width: 5, height: 5, border: "none" }} />
      <div className="flex items-center gap-2">
        <div className="w-2 h-2 rounded-full shrink-0" style={{ background: meta.color }} />
        <div className="flex-1 min-w-0">
          <div className="text-[12px] font-medium text-zinc-100 truncate leading-tight">{data.label}</div>
          <div className="text-[9px] truncate flex items-center gap-1.5">
            <span style={{ color: meta.color }}>{meta.label}</span>
            <span style={{ color: EPI_COLOR[data.epistemic] || "#EF9F27" }}>
              {EPI_LABEL[data.epistemic] || "Inféré"}
            </span>
            {data.origin === "linked" && <span className="text-zinc-500">reliée</span>}
            {!data.exists && <span className="text-zinc-500">supprimé depuis</span>}
          </div>
        </div>
      </div>
      <Handle type="source" position={Position.Right} style={{ background: meta.color, width: 5, height: 5, border: "none" }} />
    </div>
  );
}

const nodeTypes = { eco: EcoNode };

function layoutCircular(nodes) {
  const entities = nodes.filter((n) => n.group === "entity");
  const items = nodes.filter((n) => n.group !== "entity");
  const place = (arr, radius, offset = 0) =>
    arr.map((n, i) => {
      const angle = (2 * Math.PI * i) / Math.max(arr.length, 1) + offset;
      return { ...n, position: { x: Math.cos(angle) * radius, y: Math.sin(angle) * radius * 0.7 } };
    });
  if (nodes.length === 1) return [{ ...nodes[0], position: { x: 0, y: 0 } }];
  return [...place(entities, entities.length > 1 ? 240 : 0), ...place(items, 480, Math.PI / 6)];
}

export default function EcosystemGraphModal({ deploymentId, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [stack, setStack] = useState([]);

  useEffect(() => {
    if (!deploymentId) return;
    setData(null);
    setError("");
    api.get(`/workspace/agentic/ecosystem/deployments/${deploymentId}/graph`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "Chargement du schéma impossible."));
  }, [deploymentId]);

  const rfNodes = useMemo(() => {
    if (!data) return [];
    return layoutCircular(data.nodes || []).map((n) => {
      const navType = NAV_OTYPE[n.otype];
      return {
        id: n.id,
        type: "eco",
        position: n.position,
        draggable: true,
        data: {
          oid: n.id,
          label: n.label,
          otype: n.otype,
          epistemic: n.epistemic,
          origin: n.origin,
          exists: n.exists,
          clickable: !!(navType && n.exists),
          onOpen: navType && n.exists
            ? () => setStack([ontologyPanel(navType, n.id, n.label)])
            : undefined,
        },
      };
    });
  }, [data]);

  const rfEdges = useMemo(() => {
    if (!data) return [];
    return (data.edges || []).map((e) => {
      const c = EPI_COLOR[e.epistemic] || "#EF9F27";
      return {
        id: e.id,
        source: e.from,
        target: e.to,
        label: e.label,
        labelStyle: { fill: "#D4D4D8", fontSize: 9 },
        labelBgStyle: { fill: "#0E0E10", fillOpacity: 0.85 },
        style: {
          stroke: `${c}99`,
          strokeWidth: 1.4,
          strokeDasharray: e.epistemic === "inferred" ? "5 4" : undefined,
        },
        markerEnd: { type: MarkerType.ArrowClosed, color: c, width: 10, height: 10 },
      };
    });
  }, [data]);

  const dep = data?.deployment;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/75 backdrop-blur flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="ecosystem-graph-overlay"
    >
      <div
        className="noam-card w-full max-w-4xl p-5"
        onClick={(e) => e.stopPropagation()}
        data-testid="ecosystem-graph-modal"
      >
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="noam-overline text-[#7F77DD] inline-flex items-center gap-1.5">
              <Network className="w-3.5 h-3.5" /> Schéma d'écosystème
            </div>
            <div className="text-base font-semibold text-zinc-100 mt-1">
              {dep?.label || "Grappe déployée"}
            </div>
            <div className="text-[10px] text-zinc-500 mt-0.5">
              Dérivé des objets et relations validés
              {dep?.created_at ? ` · déployé le ${new Date(dep.created_at).toLocaleString("fr-CH")}` : ""}
              {dep?.user_email ? ` par ${dep.user_email}` : ""} · cliquez un nœud pour ouvrir sa fiche canonique
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded hover:bg-white/5 text-zinc-400"
            data-testid="ecosystem-graph-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="rounded-lg border border-white/10 overflow-hidden" style={{ height: 440 }}>
          {error ? (
            <div className="p-8 text-center text-sm text-[#EF9F27]">{error}</div>
          ) : !data ? (
            <div className="p-8 text-sm text-zinc-500 flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Construction du schéma…
            </div>
          ) : rfNodes.length === 0 ? (
            <div className="p-8 text-center text-sm text-zinc-500">Aucun objet matérialisé dans ce déploiement.</div>
          ) : (
            <ReactFlow
              nodes={rfNodes}
              edges={rfEdges}
              nodeTypes={nodeTypes}
              fitView
              fitViewOptions={{ padding: 0.25 }}
              colorMode="dark"
              proOptions={{ hideAttribution: true }}
              nodesConnectable={false}
              minZoom={0.2}
              maxZoom={2}
            >
              <Background gap={24} color="#1f2937" />
            </ReactFlow>
          )}
        </div>

        <div className="mt-3 flex items-center gap-3 flex-wrap text-[10px] text-zinc-400" data-testid="ecosystem-graph-legend">
          {Object.entries(EPI_LABEL).map(([k, v]) => (
            <span key={k} className="inline-flex items-center gap-1.5">
              <span className="inline-block w-4 border-t-2" style={{ borderColor: EPI_COLOR[k], borderStyle: k === "inferred" ? "dashed" : "solid" }} />
              {v}
            </span>
          ))}
          <span className="text-zinc-500 ml-auto italic">Le statut épistémique est conservé après validation.</span>
        </div>
      </div>

      <ContextStack
        stack={stack}
        rootLabel="Schéma"
        push={(p) => setStack((st) => [...st, p])}
        onPop={(n) => setStack((st) => st.slice(0, n))}
        onClose={() => setStack([])}
      />
    </div>
  );
}
